import _ from "lodash";
import React, { useState } from "react";
import { Alert, Button, Modal, TextInput } from "@toddle-edu/ds-web";
import classes from "./AssessmentCategoriesDeleteModal.scss";
import { useI18n } from "Hooks";
import { graphql, compose } from "lib/apolloClient";
import {
  getAssessmentsMappedToCategorySetQuery,
  getAssessmentsMappedToCategoryQuery,
} from "ScoreCategories/modules/ScoreCategoriesQueries";
import {
  getAssessmentsMappedToCategorySetFromCache,
  getAssessmentsMappedToCategoryFromCache,
} from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";
import { Trans } from "react-i18next";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { elevations } from "@toddle-edu/ds-theme";
import classNames from "classnames";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getAssessmentCategoriesDeleteModalLocaleKeys } from "ScoreCategories/modules/Utils";

const styles = {
  popoverArrowStyles: {
    opacity: 1,
    zIndex: 2500,
    right: "6px",
    top: 0,
  },
};

const MappedAssessmentsList = ({ mappedAssessments, itemLabel, t }) => {
  const mappedAssessmentListHeaderClass = classNames("text-label-2");
  const assessmentListClass = classNames(
    "text-body-2",
    classes.assessmentListContainer
  );

  return (
    <>
      <div className={mappedAssessmentListHeaderClass}>
        {t("academicSetup:assessments_linked_to_label", {
          label: itemLabel,
        })}
      </div>
      <ul className={assessmentListClass}>
        {_.map(mappedAssessments, assessment => (
          <li>{assessment}</li>
        ))}
      </ul>
    </>
  );
};

const LocalisedBodyText = React.memo(({ itemType }) => {
  if (itemType === "CATEGORY_SET") {
    return (
      <Trans i18nKey="academicSetup:deleting_category_set_would_unlink_the_assessments_with_number_of_assessments">
        are linked to categories in this category set. Deleting this category
        set would <b>unlink the assessments</b> from their respective
        categories. This would also
        <b>affect class progress summary scores</b> and
        <b>unlocked progress reports.</b>
      </Trans>
    );
  } else {
    return (
      <Trans i18nKey="academicSetup:deleting_category_would_unlink_the_assessments_with_number_of_assessments">
        are linked to this category. Deleting this category would
        <b>unlink the assessments</b> from the category. This would also
        <b>affect class progress summary scores</b> and
        <b>unlocked progress reports.</b>
      </Trans>
    );
  }
});

LocalisedBodyText.displayName = "LocalisedBodyText";

const BodyWithMappedAssessments = ({
  mappedAssessments,
  itemLabel,
  t,
  setIsDeleteConfirmationChecked,
  confirmationInputText,
  setConfirmationInputText,
  localeKeyForItem,
  confirmationText,
  itemType,
  showUpdatedDesign = false,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const togglePopover = () => setShowPopover(prevState => !prevState);

  const assessmentsCount = _.size(mappedAssessments);
  const handleClickCheckbox = () =>
    setIsDeleteConfirmationChecked(prevState => !prevState);
  const handleInputChange = e => setConfirmationInputText(e?.target?.value);

  return (
    <>
      <div className={classes.modalBodyTextContainer}>
        <Popover
          positions={["left"]}
          reposition={false}
          containerClassName={classes.popoverContainer}
          isOpen={showPopover}
          boundaryInset={10}
          content={({ position, childRect, popoverRect }) => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor={"white"}
              arrowSize={10}
              arrowStyle={styles.popoverArrowStyles}
              className="popover-arrow-container"
              arrowClassName="popover-arrow"
            >
              <div
                style={elevations["elevation-1R"]}
                className={classes.mappedAssessmentsContainer}
              >
                <MappedAssessmentsList
                  mappedAssessments={mappedAssessments}
                  itemLabel={itemLabel}
                  t={t}
                />
              </div>
            </ArrowContainer>
          )}
        >
          <span
            className={classes.assessmentNumberSpan}
            onClick={togglePopover}
          >
            {t("common:assessment_with_count_plural", {
              count: assessmentsCount,
            })}
            &nbsp;
          </span>
        </Popover>
        <LocalisedBodyText itemType={itemType} />
      </div>
      <Alert
        type={"error"}
        message={t("academicSetup:delete_item_confirmation_with_label", {
          label: itemLabel,
          item: t(localeKeyForItem),
        })}
        onChange={handleClickCheckbox}
        showIcon={false}
        dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
      />
      <div className={classes.confirmationInputContainer}>
        <TextInput
          placeholder={t("common:type_label", {
            label: confirmationText,
          })}
          label={t("academicSetup:please_type_label_below_to_confirm", {
            label: confirmationText,
          })}
          value={confirmationInputText}
          onChange={handleInputChange}
          dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
        />
      </div>
    </>
  );
};

const AssessmentCategoriesDeleteModal = ({
  itemId,
  itemLabel,
  closeModal,
  onClickDelete,
  mappedAssessments,
  itemType,
  isLoading,
  showUpdatedDesign = false,
}) => {
  const { t } = useI18n(["common", "academicSetup"]);

  const [
    isDeleteConfirmationChecked,
    setIsDeleteConfirmationChecked,
  ] = useState(false);
  const [confirmationInputText, setConfirmationInputText] = useState("");

  const onClickDeleteButton = () => {
    if (itemType === "CATEGORY_SET") {
      onClickDelete({ templateId: itemId });
    } else {
      onClickDelete();
    }
  };

  const areAssessmentsMappedWithItem = _.size(mappedAssessments) > 0;
  const {
    localeKeyForItem,
    localeKeyForDeleteConfirmation,
  } = getAssessmentCategoriesDeleteModalLocaleKeys({ itemType });
  const confirmationText = t("common:delete_label", { label: itemLabel });

  const isDeleteButtonDisabled =
    areAssessmentsMappedWithItem &&
    (!isDeleteConfirmationChecked ||
      confirmationInputText !== confirmationText);

  return isLoading ? (
    <FullScreenLoader />
  ) : (
    <Modal
      width="440px"
      hasOverlay={true}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      isClosable={true}
      onClose={closeModal}
      height={"fit-content"}
      dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
    >
      <Modal.Header>
        {t("academicSetup:delete_item_with_label", {
          label: itemLabel,
          item: t(localeKeyForItem),
        })}
      </Modal.Header>
      <Modal.Body>
        {areAssessmentsMappedWithItem ? (
          <BodyWithMappedAssessments
            mappedAssessments={mappedAssessments}
            itemLabel={itemLabel}
            t={t}
            setIsDeleteConfirmationChecked={setIsDeleteConfirmationChecked}
            confirmationInputText={confirmationInputText}
            setConfirmationInputText={setConfirmationInputText}
            localeKeyForItem={localeKeyForItem}
            confirmationText={confirmationText}
            itemType={itemType}
            showUpdatedDesign={showUpdatedDesign}
          />
        ) : (
          t(localeKeyForDeleteConfirmation, { label: itemLabel })
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerContainer}>
          <Button
            _legacy_size="large"
            size="medium"
            _legacy_variant="outlined-subtle"
            variant="neutral"
            type="outlined"
            onClick={closeModal}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          >
            {t("common:cancel")}
          </Button>
          <Button
            _legacy_size="large"
            size="medium"
            disabled={isDeleteButtonDisabled}
            onClick={onClickDeleteButton}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          >
            {t("common:delete")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default compose(
  graphql(getAssessmentsMappedToCategorySetQuery, {
    name: "getAssessmentsMappedToCategorySet",
    skip: ({ itemType }) => itemType !== "CATEGORY_SET",
    options: ({ itemId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: itemId, portalType },
    }),
    props({ getAssessmentsMappedToCategorySet, ownProps: { itemId } }) {
      const queryData = getAssessmentsMappedToCategorySetFromCache({
        variables: {
          id: itemId,
        },
      });

      const assessmentsMappedToCategorySet = _.flatten(
        _.map(_.get(queryData, "academicCriteriaValues", []), category =>
          _.map(_.get(category, "assignments", []), assignment =>
            _.get(assignment, "content.title.value", "")
          )
        )
      );

      return {
        mappedAssessments: assessmentsMappedToCategorySet,
        isLoading: getAssessmentsMappedToCategorySet?.loading,
        isData: !_.isEmpty(queryData),
      };
    },
  }),
  graphql(getAssessmentsMappedToCategoryQuery, {
    name: "getAssessmentsMappedToCategory",
    skip: ({ itemType, itemId }) =>
      itemType !== "INDIVIDUAL_CATEGORY" || _.isEmpty(itemId),
    options: ({ itemId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: itemId, portalType },
    }),
    props({ getAssessmentsMappedToCategory, ownProps: { itemId } }) {
      const queryData = getAssessmentsMappedToCategoryFromCache({
        variables: {
          id: itemId,
        },
      });

      const assessmentsMappedToCategory = _.map(
        _.get(queryData, "assignments", []),
        assignment => _.get(assignment, "content.title.value", "")
      );

      return {
        mappedAssessments: assessmentsMappedToCategory,
        isLoading: getAssessmentsMappedToCategory?.loading,
        isData: !_.isEmpty(queryData),
      };
    },
  })
)(AssessmentCategoriesDeleteModal);
